<!-- Widget that displays a Ticker as a list item. -->

<template>
  <div>
    <v-list
      dense
      two-line
    >
      <v-list-tile
        class="
          ticker-values ticker-tile
          mt-1
          list-tile-align-start list-tile-no-left-padding
        "
        :ripple="false"
        @click="defaultAction()"
      >
        <content-selection-checkbox
          :show="showContentSelectionCheckbox"
          :content="ticker"
          :class="{'mr-2': $vuetify.breakpoint.smAndUp}"
        />
        <img
          v-show="!tickerPreviewRunning"
          v-if="ticker.is_new"
          class="newbadge"
          :src="$store.state.common.newBadgeURL"
        >
        <v-layout v-bind="previewLayout">
          <!-- Note: Don't add 'grow-0' here as it looks wrong on iOS.
                       Also don't add 'height: 100%', list-tile would grow on
                       hover in IE11 and Edge when wrapped in 'height: auto'
                       container.
          -->
          <div class="ticker-item-group mb-1">
            <div
              v-if="showPreview"
              class="ticker-item clickable"
            >
              <mini-preview-ticker
                :message="ticker.message"
                :full="$vuetify.breakpoint.xs"
                :medium="$vuetify.breakpoint.mdAndUp"
                :small="$vuetify.breakpoint.sm"
                :class="{
                  'auto-height auto-width mb-2': tickerPreviewRunning,
                }"
                :style="{
                  'margin-right': $vuetify.breakpoint.xs ? '' : '1.5em',
                }"
                @click.native.stop
                @started="tickerPreviewRunning = true"
                @stopped="tickerPreviewRunning = false"
              />
            </div>
          </div>
          <div class="grow-3">
            <!-- Message -->
            <div
              v-if="showMessage"
              class="ticker-item ticker-message clickable"
              :class="{'msr--text': highlightTitle}"
              @click.stop="goToTicker(ticker.id)"
            >
              <q class="ticker-quote">{{ ticker.message.trim() }}</q>
            </div>
            <!-- Date -->
            <div
              v-if="showDate"
              class="spot-item category grey--text text--darken-1"
            >
              <span v-if="ticker.date">{{
                ticker.date | dayjs('DD.MM.YYYY')
              }}</span>
            </div>
            <!-- Author -->
            <div
              class="ticker-item category grey--text text--darken-1 mt-1"
              @click.stop="goToTicker(ticker.id)"
            >
              <span v-if="ticker.creator && showAuthor">
                <v-layout wrap>
                  {{ $t('from').toLowerCase() }}&nbsp;
                  <span>{{ ticker.creator.first_name || '' }}&nbsp;</span>
                  <span>{{ ticker.creator.last_name || '' }}</span>
                </v-layout>
              </span>
              <span v-else>{{ $t('ticker') }}</span>
            </div>
          </div>
        </v-layout>
        <div
          class="ticker-item-group"
          style="align-self: center"
        >
          <div class="ticker-item actions">
            <slot name="actions">
              <v-layout
                row
                align-center
              >
                <on-air-indicator
                  :is-on-air="ticker.is_on_air"
                  :is-planned-to-air="ticker.is_planned_to_air"
                  data-cy="ticker-on-air-indicator"
                />
              </v-layout>
            </slot>
          </div>
        </div>
      </v-list-tile>
    </v-list>
    <v-divider v-if="showDivider" />
  </div>
</template>

<script>
import ContentSelectionCheckbox from '@/components/ContentSelectionCheckbox';
import MiniPreviewTicker from '@/components/MiniPreviewTicker';
import OnAirIndicator from '@/components/OnAirIndicator';

export default {
  components: {
    ContentSelectionCheckbox,
    MiniPreviewTicker,
    OnAirIndicator,
  },
  props: {
    /* A TickerMessage object. */
    ticker: {
      type: Object,
      required: true,
    },
    highlightTitle: {
      type: Boolean,
      default: false,
    },
    /* Optionally override the default click handler for the v-list-tile. */
    onClick: {
      type: Function,
      default: null,
    },
    /* All props below control column visibility. */
    showPreview: {
      type: Boolean,
      default: true,
    },
    showMessage: {
      type: Boolean,
      default: true,
    },
    showAuthor: {
      type: Boolean,
      default: true,
    },
    showDate: {
      type: Boolean,
      default: false,
    },
    showContentSelectionCheckbox: {
      type: Boolean,
      default: false,
    },
    showDivider: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tickerPreviewRunning: false,
    };
  },
  computed: {
    previewLayout() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return {row: true};
      }
      return {column: true};
    },
  },
  methods: {
    defaultAction() {
      if (this.onClick) {
        this.onClick();
      } else {
        this.goToTicker(this.ticker.id);
      }
    },
    goToTicker(tickerId) {
      let path = '/ticker/' + tickerId;
      this.$router.push(path);
    },
  },
};
</script>

<style>
/* Ensure full width v-list-tile. */
.ticker-tile .v-list__tile {
  width: 100%;
}
.v-list--two-line.v-list--dense .ticker-tile .v-list__tile {
  height: auto;
}
</style>

<style scoped>
.ticker-values {
  display: flex;
}

.ticker-item {
  word-break: break-word;
}
.ticker-item.last {
  margin-right: 0;
}
.ticker-item.actions {
  margin-left: 1em;
  justify-content: flex-end;
}

.ticker-item-group {
  display: flex;
  flex-flow: row nowrap;
}
.ticker-item-group.last {
  justify-content: flex-end;
  align-items: flex-start;
}
@media (max-width: 599px) {
  .ticker-item-group {
    flex-flow: column nowrap;
  }
  .ticker-item-group.last {
    justify-content: flex-start;
    align-items: flex-end;
  }
}

.category {
  min-width: 80px;
}

.ticker-quote {
  font-size: 1.15em;
  font-weight: lighter;
  font-style: italic;
}
</style>



// WEBPACK FOOTER //
// src/components/Ticker.vue